import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PermissionCode } from '../lib/permissions';

interface User {
  userId: number;
  username: string;
  email: string;
  fullName: string;
  roleNames: string[];
  facilityIds: number[];
  /** Login departments this account may work in (F3). Empty for most accounts
   *  today — the association is assigned by an administrator, and an account
   *  without one behaves exactly as every account did before F3. */
  departmentIds: number[];
  /** The department this SESSION is working in, as reported by the server from
   *  the access token. `null` when the account holds several with no primary
   *  (the user is prompted to pick) or holds none.
   *
   *  Server-sourced on purpose: this is the value stamped on any encounter the
   *  session opens, so the badge in the top bar and the column written by
   *  `arrive` / `check_in` resolve from one place. A locally-remembered choice
   *  would let the UI claim attribution that is not happening. */
  activeDepartmentId: number | null;
  isActive: boolean;
  isMfaEnabled: boolean;
  /** Whether the account holds a local password (R-2.8). False for SSO-only
   *  accounts — the break-glass dialog offers a TOTP code instead of password
   *  re-entry. Sourced from /auth/profile like the rest of the session.
   *  Optional so persisted pre-R-2.8 sessions (and hand-built test users)
   *  still typecheck — absent reads as password mode, today's behaviour. */
  hasPassword?: boolean;
  avatarUrl?: string | null;
  timezone?: string | null;
  /**
   * BCP-47 locale code (e.g. "en", "zh-CN") sourced from the backend
   * UserInfo payload on /auth/login. Persisted alongside the rest of
   * the session so a page refresh can rehydrate i18next without a
   * /auth/profile round-trip. AuthProvider.login() also feeds this
   * value into `i18n.changeLanguage` and the `vynaemr-language`
   * localStorage key (which the LanguageDetector reads at boot).
   */
  preferredLanguage?: string | null;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  // Server returns arbitrary strings; we keep the wire type loose.
  // The hasPermission() API below accepts only PermissionCode so
  // callers get the compile-time check even though we can't prevent
  // the server from sending unknown codes.
  permissions: string[];
  selectedFacilityId: number | null;
  isAuthenticated: boolean;
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setPermissions: (permissions: string[]) => void;
  // S5-043: nullable — "All facilities" resets to null (header omitted),
  // never to Number('')===0 (a stamped X-Facility-Id: 0 that 403s premium
  // modules with a misdirecting scope-denial instead of FACILITY_REQUIRED).
  selectFacility: (facilityId: number | null) => void;
  logout: () => void;
  // Accept plain string so callers passing a computed perm code
  // (e.g. loops over a permission list) still compile. Prefer
  // PERMS.X everywhere — the PERMS object is typed so you get
  // autocomplete + compile-time typo catches at the call site.
  hasPermission: (permission: PermissionCode | string) => boolean;
  /**
   * True when the user holds *any* of the listed perms. Handy for
   * buttons gated by an umbrella code (BILLING_CREATE) OR any of
   * its specific splits (CODING_ASSIGN / CLAIM_SUBMIT / ...).
   */
  hasAnyPermission: (permissions: readonly (PermissionCode | string)[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      permissions: [],
      selectedFacilityId: null,
      isAuthenticated: false,
      setUser: (user) => set({ user, isAuthenticated: true }),
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      setPermissions: (permissions) => set({ permissions }),
      selectFacility: (facilityId) => set({ selectedFacilityId: facilityId }),
      // NB there is no `selectDepartment` counterpart to `selectFacility`.
      // Changing the login department re-mints the session server-side
      // (POST /auth/switch-department) because the department travels in the
      // token and is stamped on encounters from there. A local setter would
      // move the badge without moving the attribution.
      logout: () => set({
        user: null,
        accessToken: null,
        refreshToken: null,
        permissions: [],
        selectedFacilityId: null,
        isAuthenticated: false,
      }),
      hasPermission: (permission) => get().permissions.includes(permission),
      hasAnyPermission: (permissions) =>
        permissions.some((p) => get().permissions.includes(p)),
    }),
    { name: 'vynaemr-auth' }
  )
);
